import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Headset, Loader2, CheckCircle2, RotateCcw, Mail, User } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const FILTERS = [
  { id: "all", label: "Todos" },
  { id: "open", label: "Abertos" },
  { id: "resolved", label: "Resolvidos" },
];

function dt(s) {
  if (!s) return "";
  return new Date(s).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
}

export default function AdminTickets() {
  const [filter, setFilter] = useState("open");
  const [data, setData] = useState({ tickets: [], open_count: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/admin/support/tickets?status=${filter}`).then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };
  useEffect(load, [filter]); // eslint-disable-line

  const setStatus = async (t, status) => {
    setBusy(true);
    try {
      await api.patch(`/admin/support/tickets/${t.id}`, { status });
      toast.success(status === "resolved" ? "Ticket marcado como resolvido." : "Ticket reaberto.");
      setSel((s) => (s && s.id === t.id ? { ...s, status } : s));
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-tickets">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          {FILTERS.map((f) => (
            <button key={f.id} data-testid={`ticket-filter-${f.id}`} onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 rounded-xl text-sm border transition-colors ${filter === f.id ? "border-[#FF4D00]/50 bg-[#FF4D00]/10 text-[#FF4D00]" : "border-white/10 text-zinc-400 hover:text-zinc-200"}`}>
              {f.label}
            </button>
          ))}
        </div>
        <span className="text-xs text-zinc-500" data-testid="ticket-open-count">{data.open_count} aberto(s) · {data.total} no total</span>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-zinc-500 py-12 justify-center"><Loader2 className="h-5 w-5 animate-spin" />Carregando...</div>
      ) : data.tickets.length === 0 ? (
        <div className="text-zinc-500 bg-card border border-dashed border-white/10 rounded-xl p-12 text-center flex flex-col items-center gap-2">
          <Headset className="h-8 w-8 text-zinc-600" />Nenhum chamado {filter === "open" ? "aberto" : filter === "resolved" ? "resolvido" : ""} por aqui.
        </div>
      ) : (
        <div className="space-y-2">
          {data.tickets.map((t) => (
            <button key={t.id} data-testid={`ticket-row-${t.id}`} onClick={() => setSel(t)}
              className="w-full text-left flex items-center gap-3 bg-card border border-white/[0.06] rounded-xl p-4 hover:border-[#FF4D00]/30 transition-colors">
              <span className={`h-9 w-9 shrink-0 rounded-full flex items-center justify-center ${t.status === "resolved" ? "bg-emerald-500/10 text-emerald-400" : "bg-[#FF4D00]/10 text-[#FF4D00]"}`}><Headset className="h-4 w-4" /></span>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{t.subject || "Solicitação de atendimento"}</div>
                <div className="text-xs text-zinc-500 truncate">{t.name || "—"} · {t.email}{t.plan ? ` · ${t.plan}` : ""}</div>
              </div>
              <div className="text-right shrink-0">
                <span className={`text-[10px] px-2 py-0.5 rounded-xl border font-semibold ${t.status === "resolved" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" : "border-[#FF4D00]/40 bg-[#FF4D00]/10 text-[#FF4D00]"}`}>{t.status === "resolved" ? "Resolvido" : "Aberto"}</span>
                <div className="text-[10px] text-zinc-600 mt-1 mono">{dt(t.created_at)}</div>
              </div>
            </button>
          ))}
        </div>
      )}

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="bg-card border-white/10 max-w-lg max-h-[85vh] overflow-y-auto" data-testid="ticket-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Headset className="h-5 w-5 text-[#FF4D00]" />{sel?.subject || "Chamado"}</DialogTitle></DialogHeader>
          {sel && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-zinc-300"><User className="h-4 w-4 text-zinc-500" />{sel.name || "—"}{sel.plan ? <span className="text-xs px-1.5 py-0.5 rounded-xl border border-white/10 text-zinc-400">{sel.plan}</span> : null}</div>
              <a href={`mailto:${sel.email}`} className="flex items-center gap-2 text-sm text-accent hover:underline"><Mail className="h-4 w-4" />{sel.email}</a>
              <div className="text-[11px] text-zinc-600 mono">Aberto em {dt(sel.created_at)}{sel.resolved_at ? ` · Resolvido em ${dt(sel.resolved_at)}` : ""}</div>
              <div>
                <div className="text-xs uppercase tracking-wide text-zinc-500 mb-1">Conversa com o assistente</div>
                <pre className="whitespace-pre-wrap text-xs text-zinc-300 bg-[#09090B] border border-white/10 rounded-xl p-3 max-h-64 overflow-y-auto" data-testid="ticket-transcript">{sel.transcript || "Sem histórico de conversa."}</pre>
              </div>
              {sel.status === "resolved" ? (
                <Button data-testid="ticket-reopen-btn" onClick={() => setStatus(sel, "open")} disabled={busy} variant="outline" className="w-full rounded-xl bg-transparent border-white/10 gap-2"><RotateCcw className="h-4 w-4" />Reabrir chamado</Button>
              ) : (
                <Button data-testid="ticket-resolve-btn" onClick={() => setStatus(sel, "resolved")} disabled={busy} className="w-full rounded-xl bg-emerald-600 hover:bg-emerald-700 gap-2">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}Marcar como resolvido</Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
